import { CheckedAddress } from '@sparkdotfi/common-universal'
import { QueryKey, queryOptions, UseQueryOptions, useSuspenseQueries } from '@tanstack/react-query'
import { zip } from 'remeda'
import { Address, zeroAddress } from 'viem'
import { Config, useChainId, useConfig } from 'wagmi'

import { allowance } from '../flavours/approve/logic/query'
import { ChainConfigEntry } from '@/config/chain/types'
import { useChainConfigEntry } from '@/domain/hooks/useChainConfigEntry'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export interface TokenApprovalResult {
  token: CheckedAddress
  spender: Address
  approval: bigint
}

export function useTokenApprovals(): TokenApprovalResult[] {
  const chainId = useChainId()
  const { account = CheckedAddress(zeroAddress) } = useConnectedAddress({ throwOnNotConnected: false }) // stub to have a valid address
  const wagmiConfig = useConfig()
  const chainConfig = useChainConfigEntry()

  const { queries, contracts } = getApprovalsQueryOptions({ chainId, account, wagmiConfig, chainConfig })

  return useSuspenseQueries({
    queries,
    combine: (combinedResults) =>
      zip(combinedResults, contracts).map(([result, contract]) => ({
        token: contract.token,
        spender: contract.contract,
        approval: result.data,
      })),
  })
}

interface UseApprovalsQueryOptionsParams {
  chainId: number
  account: Address
  wagmiConfig: Config
  chainConfig: ChainConfigEntry
}

export function getApprovalsQueryOptions({
  chainId,
  account,
  wagmiConfig,
  chainConfig,
}: UseApprovalsQueryOptionsParams): {
  queries: UseQueryOptions<bigint, Error, bigint, QueryKey>[]
  contracts: { token: CheckedAddress; contract: Address }[]
} {
  const contracts = chainConfig.tokensRequiringResetApproval.flatMap((token) =>
    chainConfig.contractsRequiringUsdtResetApproval.map((contract) => ({ token, contract })),
  )

  const queries = contracts.map(({ token, contract }) => {
    return queryOptions({
      ...allowance({ token, spender: contract, wagmiConfig, account, chainId }),
    })
  })

  return {
    queries,
    contracts,
  }
}
